// src/dynamoService.js
import awsconfig from './aws-exports'; 
import { v4 as uuidv4 } from 'uuid'; 
import { Auth } from 'aws-amplify'; 

// --- API GATEWAY INVOKE URLS ---
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const API_GALLERY_URL = `${API_BASE_URL}/items`; 
const API_UPLOAD_URL = `${API_BASE_URL}/upload`; 
const API_PRESIGNED_URL_GENERATOR = `${API_BASE_URL}/presigned-url`; 
const API_USER_ITEMS_BASE_URL = `${API_BASE_URL}/user/items`; 

// Helper to get authenticated user token
async function getAuthToken() { 
    try { 
        const session = await Auth.currentSession(); 
        return session.getIdToken().getJwtToken(); 
    } catch (authError) { 
        console.error("Error getting Amplify user session/token:", authError); 
        return null; 
    } 
} 

// =========================================================
// 1. UPLOAD FUNCTION (S3 + DynamoDB)
// =========================================================
export const saveItemToDynamoDB = async (itemData, imageFile) => { 
    const userToken = await getAuthToken(); 
    if (!userToken) { 
        throw new Error("User not authenticated. Please log in to upload an item."); 
    } 

    let imageUrl = null; 

    // --- S3 Upload Logic (omitted for brevity, assume returns public URL) ---
    if (imageFile) { 
        try { 
            // Simplified fetch logic for presigned URL and PUT request...
            const presignedUrlResponse = await fetch(
                `${API_PRESIGNED_URL_GENERATOR}?fileName=${encodeURIComponent(itemData.reporterName)}-${uuidv4()}-${encodeURIComponent(imageFile.name)}&fileType=${encodeURIComponent(imageFile.type)}&itemType=${encodeURIComponent(itemData.itemType)}`,
                { headers: { 'Authorization': `Bearer ${userToken}` } }
            );

            if (!presignedUrlResponse.ok) {
                const errorData = await presignedUrlResponse.json();
                throw new Error(`Failed to get presigned URL: ${errorData.message || 'Unknown error'}`);
            }

            const { uploadUrl, fileUrl: receivedImageUrl } = await presignedUrlResponse.json();
            imageUrl = receivedImageUrl;

            const s3UploadResponse = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': imageFile.type }, body: imageFile });
            if (!s3UploadResponse.ok) { throw new Error(`Failed to upload image to S3: ${s3UploadResponse.statusText}`); }
            console.log("Image successfully uploaded to S3!");

        } catch (error) { 
            console.error("Error during image upload process:", error); 
            throw error; 
        } 
    } else { 
        throw new Error("An image file is required for item upload."); 
    } 

    // --- DYNAMODB METADATA PAYLOAD ---
    const dataToSend = { 
        itemId: uuidv4(), 
        status: "active", 
        itemName: itemData.itemName, 
        description: itemData.description, 
        location: itemData.location, 
        createdAt: itemData.dateLost, 
        itemType: itemData.itemType, 
        imageUrl: imageUrl, 
        // CRUCIAL CHANGE: Combine name and contact info into a single field for backend storage
        contactInfo: `${itemData.reporterName} | ${itemData.contactInfo}` // Example: "Ravi Kumar | 999-555-1234"
    }; 

    console.log("Data being sent to /upload:", dataToSend); 

    try { 
        const response = await fetch(API_UPLOAD_URL, { 
            method: 'POST', 
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${userToken}` }, 
            body: JSON.stringify(dataToSend), 
        }); 

        if (!response.ok) { 
            const errorBody = await response.json(); 
            throw new Error(errorBody.message || `HTTP error! status: ${response.status}`); 
        } 

        const result = await response.json(); 
        console.log("Item metadata saved successfully (via API Gateway)!", result); 
        // Verification code is no longer needed in the frontend response
        return { itemId: dataToSend.itemId, message: result.message }; 
    } catch (error) { 
        console.error("Error saving item metadata via API Gateway:", error); 
        throw error; 
    } 
}; 

// =========================================================
// 2. GALLERY FETCH (Public Access)
// =========================================================
export const getLostItemsFromDynamoDB = async () => { 
    try { 
        const response = await fetch(API_GALLERY_URL); 
        if (!response.ok) { 
            const errorBody = await response.json(); 
            throw new Error(errorBody.message || `HTTP error! status: ${response.status}`); 
        } 

        const data = await response.json(); 
        return data; 
    } catch (error) { 
        console.error("Error fetching items from API Gateway (Gallery):", error); 
        return []; 
    } 
}; 

// =========================================================
// 3. PROFILE FETCH: UPLOADED ITEMS (Authenticated Access)
// =========================================================
export const getUploadedItemsFromDynamoDB = async () => { 
    const userToken = await getAuthToken(); 
    if (!userToken) { throw new Error("User not authenticated for fetching uploaded items."); } 

    try { 
        const url = `${API_USER_ITEMS_BASE_URL}?type=uploaded`; 
        const response = await fetch(url, { 
            method: 'GET', 
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${userToken}` } 
        }); 

        if (!response.ok) { 
            const errorBody = await response.json(); 
            if (response.status === 401 || response.status === 403) { throw new Error("Unauthorized to view your uploaded items."); } 
            throw new Error(errorBody.message || `HTTP error! status: ${response.status}`); 
        } 

        const data = await response.json(); 
        return data; 
    } catch (error) { 
        console.error("Error fetching user uploaded items via API Gateway:", error); 
        throw error; 
    } 
}; 

// =========================================================
// 4. PROFILE FETCH: CLAIMED ITEMS (Authenticated Access - NOW REMOVED FROM UI)
// =========================================================
export const getClaimedItemsFromDynamoDB = async () => { 
    const userToken = await getAuthToken(); 
    if (!userToken) { throw new Error("User not authenticated for fetching claimed items."); } 

    try { 
        const url = `${API_USER_ITEMS_BASE_URL}?type=claimed`; 
        const response = await fetch(url, { 
            method: 'GET', 
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${userToken}` } 
        }); 

        if (!response.ok) { 
            const errorBody = await response.json(); 
            if (response.status === 401 || response.status === 403) { throw new Error("Unauthorized to view your claimed items."); } 
            throw new Error(errorBody.message || `HTTP error! status: ${response.status}`); 
        } 

        const data = await response.json(); 
        return data; 
    } catch (error) { 
        console.error("Error fetching user claimed items via API Gateway:", error); 
        throw error; 
    } 
};